import {
  PersonOutlined,
} from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Shared header for all logged-in pages.
 *
 * - Full-width yellow bar with drop-shadow.
 * - Left: optional content slot (e.g. SOS button on chat page).
 * - Right (desktop sm+): round nav buttons matching mobile footer style.
 * - Right (mobile xs): empty; nav lives in footer.
 *
 * See PROJECT DOCS/JEFFSAYS_STYLING.md §5 for full spec.
 */

interface AppHeaderProps {
  leftContent?: React.ReactNode;
  title?: string;
  backgroundColor?: string;
  titleColor?: string;
}

// -- Main export -------------------------------------------------------------
export default function AppHeader(
  { leftContent, title, backgroundColor = '#FFD400', titleColor = '#333' }: AppHeaderProps = {},
): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const isProfileActive = location.pathname === '/profile';

  return (
    <Box
      sx={{
        bgcolor: backgroundColor,
        width: '100%',
        height: 72,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2,
        boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.3)',
        flexShrink: 0,
        position: 'relative',
        zIndex: 10,
      }}
    >
      {/* Left: optional content (e.g. SOS button) */}
      {leftContent || <Box />}

      {/* Centre: page title */}
      {title && (
        <Typography
          sx={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            fontFamily: 'Poppins, system-ui, sans-serif',
            fontWeight: 700,
            fontSize: '1.25rem',
            color: titleColor,
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </Typography>
      )}

      {/* Right: Profile button */}
      <IconButton
        aria-label="Profile"
        aria-selected={isProfileActive}
        onClick={() => navigate('/profile')}
        sx={{
          width: 48,
          height: 44,
          bgcolor: isProfileActive ? '#000000' : '#AFAFAF',
          border: `1px solid ${isProfileActive ? '#000000' : '#AFAFAF'}`,
          borderRadius: '32px',
          filter: 'drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25))',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          transform: isProfileActive ? 'scale(1.05)' : 'scale(1)',
          '&:hover': {
            bgcolor: isProfileActive ? '#222222' : '#9E9E9E',
            transform: 'scale(1.08)',
          },
          '&:active': {
            transform: 'scale(0.95)',
          },
        }}
      >
        <PersonOutlined
          sx={{
            color: isProfileActive ? '#FFD400' : '#FFFFFF',
            transition: 'color 0.2s ease',
          }}
        />
      </IconButton>
    </Box>
  );
}
